import { getCrosswordTextures } from '@/api/crossword';
import { buildCloudFrontUrl } from '@/utils/pathUtils';
import type { CrosswordStyleDraft } from '../types';

export const DEFAULT_LANGUAGE = 'en';

/** Languages the generator can build a crossword for, keyed by the code it expects */
export const CROSSWORD_LANGUAGES: Record<string, string> = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    pt: 'Portuguese',
    it: 'Italian',
    ca: 'Catalan',
    nl: 'Dutch',
    af: 'Afrikaans',
    cs: 'Czech',
    sk: 'Slovak',
    pl: 'Polish',
    hu: 'Hungarian',
    sl: 'Slovenian',
    hr: 'Croatian',
    ro: 'Romanian',
    lt: 'Lithuanian',
    lv: 'Latvian',
    et: 'Estonian',
    da: 'Danish',
    sv: 'Swedish',
    nb: 'Norwegian',
    fi: 'Finnish',
    is: 'Icelandic',
    fo: 'Faroese',
    ga: 'Irish',
    gd: 'Scottish Gaelic',
    cy: 'Welsh',
    bg: 'Bulgarian',
    ru: 'Russian',
    uk: 'Ukrainian',
    el: 'Greek',
    he: 'Hebrew',
    ar: 'Arabic',
    id: 'Indonesian',
    ms: 'Malay',
    mg: 'Malagasy',
    tr: 'Turkish',
    la: 'Latin'
};

/** Every style must carry its letters config, so presets fall back to these */
export const DEFAULT_STYLE_LETTERS = {
    font: '/Content/fonts/arial.ttf',
    fontColor: '#000000',
    fontSize: 40
};

export const DEFAULT_STYLE_PRESET: CrosswordStyleDraft = {
    presetId: 'default',
    name: 'Default',
    tileImage: '/Content/CrosswordTextures/Square - Oak.png',
    thumbnail: '/Content/CrosswordTextures/Thumbnails/square-oak-thumb.png',
    ...DEFAULT_STYLE_LETTERS
};

/** "square-gray.png" -> "Square Gray" */
const textureNameToLabel = (name: string): string =>
    name
        .replace(/\.[^.]+$/, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .trim();

export const getStylePresets = async (): Promise<CrosswordStyleDraft[]> => {
    const textures = await getCrosswordTextures();

    const presets = textures.map<CrosswordStyleDraft>((texture) => ({
        presetId: texture.Path,
        name: textureNameToLabel(texture.Name),
        tileImage: texture.Path,
        thumbnail: texture.Thumbnail || texture.Path,
        ...DEFAULT_STYLE_LETTERS
    }));

    return [DEFAULT_STYLE_PRESET, ...presets];
};

/** The Default preset ships its thumbnail with the bundle, the rest live on S3 */
export const resolveThumbnailUrl = (style: CrosswordStyleDraft): string =>
    style.thumbnail?.startsWith('/Content') ? buildCloudFrontUrl(style.thumbnail) : style.thumbnail || '';
